/**
 * Band Builder JavaScript
 * Masina Diamonds - Modern Ring/Band Builder
 * 
 * Features:
 * - Variant selection (Color, Finish, Karat)
 * - Width selection that switches products
 * - Image updates on variant change
 * - Modal functionality for Save/Request Info
 */

(function() {
  'use strict';

  class BandBuilder {
    constructor() {
      this.container = document.querySelector('.band-builder');
      if (!this.container) return;

      this.dataElement = document.getElementById('band-builder-data');
      if (!this.dataElement) return;

      try {
        this.data = JSON.parse(this.dataElement.textContent);
      } catch (e) {
        console.error('Band Builder: Failed to parse product data', e);
        return;
      }

      this.variants = this.data.variants || [];
      this.currentSelections = this.getCurrentSelections();
      this.currentVariant = this.findVariant();
      this.originalDescription = null;
      
      // Karat handling (separate products per karat)
      this.availableKarats = this.data.availableKarats || [];
      this.currentProductKarat = this.data.currentProductKarat || '14K';
      
      this.init();
    }

    init() {
      this.bindEvents();
      this.saveOriginalDescription();
      this.syncSelectionsFromUI(); // Ensure selections match current UI state
      this.applyUrlParams(); // Pre-select variants from URL params
      this.updateUI();
      this.initFloatingPreview();
    }

    syncSelectionsFromUI() {
      // Ensure currentSelections includes values from select dropdowns
      // This handles cases where options are in dropdowns rather than swatches
      const options = this.data.options || [];
      
      options.forEach((optionName, index) => {
        // If we don't have a selection for this index, try to get it from UI
        if (this.currentSelections[index] === undefined) {
          const select = this.container.querySelector(`select[data-option-index="${index}"]`);
          if (select && select.value) {
            this.currentSelections[index] = select.value;
          }
        }
      });
    }

    applyUrlParams() {
      // Read URL parameters to pre-select variant options (for karat switching)
      const params = new URLSearchParams(window.location.search);
      const options = this.data.options || [];
      let hasUrlParams = false;

      options.forEach((optionName, index) => {
        const paramValue = params.get(optionName);
        if (paramValue) {
          hasUrlParams = true;
          this.currentSelections[index] = paramValue;
          
          // Update swatch UI for this option (buttons with data-option-index)
          const swatches = this.container.querySelectorAll(`button[data-option-index="${index}"]`);
          swatches.forEach(swatch => {
            swatch.classList.remove('is-selected');
            if (swatch.dataset.optionValue === paramValue) {
              swatch.classList.add('is-selected');
            }
          });

          // Update dropdown/select UI for this option
          const select = this.container.querySelector(`select[data-option-index="${index}"]`);
          if (select) {
            // Find the option that matches (case-insensitive)
            for (let i = 0; i < select.options.length; i++) {
              if (select.options[i].value.toLowerCase() === paramValue.toLowerCase() ||
                  select.options[i].value === paramValue) {
                select.selectedIndex = i;
                break;
              }
            }
          }

          // Update value display
          const optionContainer = this.container.querySelector(`[data-option-type="${optionName.toLowerCase()}"]`);
          if (optionContainer) {
            const valueDisplay = optionContainer.querySelector('.band-builder__option-value');
            if (valueDisplay) {
              valueDisplay.textContent = paramValue.toUpperCase();
            }
          }
        }
      });

      // If URL params were present, find the matching variant
      if (hasUrlParams) {
        this.currentVariant = this.findVariant();
      }
    }

    saveOriginalDescription() {
      const descriptionEl = this.container.querySelector('.band-builder__description');
      if (descriptionEl) {
        this.originalDescription = descriptionEl.innerHTML;
      }
    }

    getCurrentSelections() {
      const selections = {};
      const variant = this.variants.find(v => v.id === this.data.currentVariantId);
      
      if (variant && variant.options) {
        variant.options.forEach((value, index) => {
          selections[index] = value;
        });
      }
      
      return selections;
    }

    bindEvents() {
      // Description Toggle
      const descriptionToggle = this.container.querySelector('[data-description-toggle]');
      if (descriptionToggle) {
        descriptionToggle.addEventListener('click', () => this.handleDescriptionToggle());
      }

      // Color & Finish Swatches
      this.container.querySelectorAll('.band-builder__swatch').forEach(swatch => {
        swatch.addEventListener('click', (e) => this.handleSwatchClick(e));
      });

      // Karat Selector - can be either variant-based or product-switcher
      const karatVariantSelect = this.container.querySelector('[data-karat-variant-selector]');
      if (karatVariantSelect) {
        // J Collection style: Karat is a variant option
        karatVariantSelect.addEventListener('change', (e) => this.handleKaratVariantChange(e));
      }
      
      const karatProductSelect = this.container.querySelector('[data-karat-product-selector]');
      if (karatProductSelect) {
        // M3 Collection style: Karat links to separate products
        karatProductSelect.addEventListener('change', (e) => this.handleKaratProductChange(e));
      }

      // Width Selector (Product Switcher)
      const widthSelect = this.container.querySelector('[data-width-selector]');
      if (widthSelect) {
        widthSelect.addEventListener('change', (e) => this.handleWidthChange(e));
      }

      // Request Info Button
      const requestBtn = this.container.querySelector('[data-request-info]');
      if (requestBtn) {
        requestBtn.addEventListener('click', () => this.openRequestModal());
      }

      // Header Add to Cart Button
      const headerCartBtn = this.container.querySelector('[data-header-add-to-cart]');
      if (headerCartBtn) {
        headerCartBtn.addEventListener('click', () => this.handleHeaderAddToCart());
      }

      // Modal Close Buttons
      document.querySelectorAll('[data-modal-close]').forEach(closeBtn => {
        closeBtn.addEventListener('click', (e) => this.closeModals(e));
      });

      // Close modal on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeModals();
        }
      });

      // Save Preview Form
      const saveForm = document.getElementById('save-preview-form');
      if (saveForm) {
        saveForm.addEventListener('submit', (e) => this.handleSavePreview(e));
      }

      // Zoom Button
      const zoomBtn = this.container.querySelector('[data-zoom-trigger]');
      if (zoomBtn) {
        zoomBtn.addEventListener('click', () => this.handleZoom());
      }

      // Form submission
      const form = document.getElementById('band-builder-form');
      if (form) {
        form.addEventListener('submit', (e) => this.handleFormSubmit(e));
      }
    }

    handleSwatchClick(e) {
      const swatch = e.currentTarget;
      
      // Check if this is a product switcher swatch (color products)
      const colorProduct = swatch.dataset.colorProduct;
      if (colorProduct) {
        this.handleColorProductSwatchClick(e);
        return;
      }

      // Handle variant-based swatch
      const optionIndex = parseInt(swatch.dataset.optionIndex, 10);
      const value = swatch.dataset.optionValue;
      const optionContainer = swatch.closest('.band-builder__option');
      const optionType = optionContainer ? optionContainer.dataset.optionType : '';

      // If selecting platinum finish, auto-swap color to white gold
      if (value && value.toLowerCase().includes('platinum')) {
        this.autoSwapToWhiteGold();
      }

      // Update selection
      this.currentSelections[optionIndex] = value;

      // Update swatch UI
      const swatchGroup = swatch.closest('.band-builder__swatches');
      swatchGroup.querySelectorAll('.band-builder__swatch').forEach(s => {
        s.classList.remove('is-selected');
      });
      swatch.classList.add('is-selected');

      // Update selected value display
      const valueDisplay = optionContainer ? optionContainer.querySelector('.band-builder__option-value') : null;
      if (valueDisplay) {
        valueDisplay.textContent = value.toUpperCase();
      }

      // Find and select new variant
      this.currentVariant = this.findVariant();
      this.updateUI();
    }

    handleColorProductSwatchClick(e) {
      const swatch = e.currentTarget;
      const colorProduct = swatch.dataset.colorProduct;
      const colorName = swatch.dataset.colorName || '';

      // If clicking current product, just update UI
      if (colorProduct === this.data.productHandle) {
        const swatchGroup = swatch.closest('.band-builder__swatches');
        swatchGroup.querySelectorAll('.band-builder__swatch').forEach(s => {
          s.classList.remove('is-selected');
        });
        swatch.classList.add('is-selected');

        const optionContainer = swatch.closest('.band-builder__option');
        const valueDisplay = optionContainer ? optionContainer.querySelector('.band-builder__option-value') : null;
        if (valueDisplay) {
          valueDisplay.textContent = colorName.toUpperCase();
        }
        return;
      }

      // Navigate to new color product, preserving variant options
      const url = new URL(`/products/${colorProduct}`, window.location.origin);
      
      // Preserve current variant options
      const options = this.data.options || [];
      options.forEach((optionName, index) => {
        const value = this.currentSelections[index];
        if (value) {
          url.searchParams.set(optionName, value);
        }
      });
      
      window.location.href = url.toString();
    }

    autoSwapToWhiteGold() {
      // When platinum is selected, auto-swap color to white gold
      const colorContainer = this.container.querySelector('[data-option-type="color"]');
      if (!colorContainer) return;

      const isProductSwitcher = colorContainer.dataset.colorSwitcher === 'true';
      
      if (isProductSwitcher) {
        // Product switcher: Find white gold swatch and navigate to that product
        const swatches = colorContainer.querySelectorAll('.band-builder__swatch');
        let whiteGoldSwatch = null;
        
        for (const swatch of swatches) {
          const colorName = (swatch.dataset.colorName || '').toLowerCase();
          if (colorName.includes('white') && !colorName.includes('platinum')) {
            whiteGoldSwatch = swatch;
            break;
          }
        }

        if (whiteGoldSwatch && whiteGoldSwatch.dataset.colorProduct !== this.data.productHandle) {
          // Navigate to white gold product
          const url = new URL(`/products/${whiteGoldSwatch.dataset.colorProduct}`, window.location.origin);
          
          // Preserve current variant options
          const options = this.data.options || [];
          options.forEach((optionName, index) => {
            const value = this.currentSelections[index];
            if (value) {
              url.searchParams.set(optionName, value);
            }
          });
          
          window.location.href = url.toString();
          return;
        }

        // Already on white gold or no white gold found - just update UI
        if (whiteGoldSwatch) {
          swatches.forEach(s => s.classList.remove('is-selected'));
          whiteGoldSwatch.classList.add('is-selected');
          const valueDisplay = colorContainer.querySelector('.band-builder__option-value');
          if (valueDisplay) {
            valueDisplay.textContent = (whiteGoldSwatch.dataset.colorName || 'WHITE GOLD').toUpperCase();
          }
        }
      } else {
        // Variant-based color: Find white gold option and select it
        const colorIndex = this.data.colorOptionIndex;
        if (colorIndex === null) return;

        const swatches = colorContainer.querySelectorAll('.band-builder__swatch');
        let whiteGoldSwatch = null;
        
        for (const swatch of swatches) {
          const colorValue = (swatch.dataset.optionValue || '').toLowerCase();
          if (colorValue.includes('white') && !colorValue.includes('platinum')) {
            whiteGoldSwatch = swatch;
            break;
          }
        }

        if (whiteGoldSwatch) {
          const whiteGoldValue = whiteGoldSwatch.dataset.optionValue;
          this.currentSelections[colorIndex] = whiteGoldValue;
          
          // Update UI
          swatches.forEach(s => s.classList.remove('is-selected'));
          whiteGoldSwatch.classList.add('is-selected');
          const valueDisplay = colorContainer.querySelector('.band-builder__option-value');
          if (valueDisplay) {
            valueDisplay.textContent = whiteGoldValue.toUpperCase();
          }
        }
      }
    }

    handleDropdownChange(e) {
      const select = e.currentTarget;
      const optionIndex = parseInt(select.dataset.optionIndex, 10);
      const value = select.value;

      // Update selection
      this.currentSelections[optionIndex] = value;

      // Find and select new variant
      this.currentVariant = this.findVariant();
      this.updateUI();
    }

    handleWidthChange(e) {
      const select = e.currentTarget;
      const isProductSwitcher = select.dataset.productSwitcher === 'true';
      
      if (isProductSwitcher) {
        // Navigate to different product, preserve current options
        const handle = select.value;
        if (handle && handle !== this.data.productHandle) {
          const url = new URL(`/products/${handle}`, window.location.origin);
          
          // Preserve all current variant options
          const options = this.data.options || [];
          options.forEach((optionName, index) => {
            const value = this.currentSelections[index];
            if (value) {
              url.searchParams.set(optionName, value);
            }
          });
          
          window.location.href = url.toString();
        }
      } else {
        // Width is a variant option - get index from data attribute or data object
        const optionIndex = select.dataset.optionIndex !== undefined 
          ? parseInt(select.dataset.optionIndex, 10)
          : this.data.widthOptionIndex;
          
        if (optionIndex !== null && optionIndex !== undefined) {
          this.currentSelections[optionIndex] = select.value;
          this.currentVariant = this.findVariant();
          this.updateUI();
        }
      }
    }

    handleKaratVariantChange(e) {
      // J Collection style: Karat is a variant option
      const select = e.currentTarget;
      const optionIndex = parseInt(select.dataset.optionIndex, 10);
      const value = select.value;

      // Update selection
      this.currentSelections[optionIndex] = value;

      // Find and select new variant
      this.currentVariant = this.findVariant();
      this.updateUI();
    }

    handleKaratProductChange(e) {
      // Karat links to separate products (M3 or J Collection style)
      const select = e.currentTarget;
      const productHandle = select.value;
      const selectedOption = select.options[select.selectedIndex];
      const selectedKarat = selectedOption.textContent.trim().toUpperCase();
      
      // Check if this is the current product (shouldn't navigate)
      if (productHandle === this.data.productHandle || selectedOption.dataset.isCurrent === 'true') {
        return;
      }
      
      // If selecting Platinum karat, color must be white gold
      // Get white gold color value to pass in URL
      let forceWhiteGoldColor = false;
      if (selectedKarat === 'PLATINUM') {
        forceWhiteGoldColor = true;
      }
      
      // Build URL with current variant options preserved
      const options = this.data.options || [];
      const params = new URLSearchParams();
      
      options.forEach((optionName, index) => {
        // First try currentSelections
        let value = this.currentSelections[index];
        
        // If not in selections, try to get from the current variant
        if (!value && this.currentVariant && this.currentVariant.options) {
          value = this.currentVariant.options[index];
        }
        
        // Also check for select elements with this option index (for width dropdown)
        if (!value) {
          const optionSelect = this.container.querySelector(`select[data-option-index="${index}"]`);
          if (optionSelect) {
            value = optionSelect.value;
          }
        }
        
        // Force white gold for platinum karat
        if (forceWhiteGoldColor && optionName.toLowerCase().includes('color')) {
          // Find white gold value from available color options
          const colorContainer = this.container.querySelector('[data-option-type="color"]');
          if (colorContainer) {
            const colorSwatches = colorContainer.querySelectorAll('.band-builder__swatch');
            for (const swatch of colorSwatches) {
              const colorName = (swatch.dataset.optionValue || swatch.dataset.colorName || '').toLowerCase();
              if (colorName.includes('white') && !colorName.includes('platinum')) {
                value = swatch.dataset.optionValue || swatch.dataset.colorName || 'White Gold';
                break;
              }
            }
          }
          // Fallback to common white gold naming
          if (!value || !value.toLowerCase().includes('white')) {
            value = 'White Gold';
          }
        }
        
        if (value) {
          params.set(optionName, value);
        }
      });
      
      // Navigate to the new karat product
      const newUrl = `/products/${productHandle}?${params.toString()}`;
      window.location.href = newUrl;
    }

    findVariant() {
      // Find variant matching current selections
      return this.variants.find(variant => {
        if (!variant.options) return false;
        
        return variant.options.every((value, index) => {
          // If we don't have a selection for this index, consider it a match
          if (this.currentSelections[index] === undefined) return true;
          return value === this.currentSelections[index];
        });
      });
    }

    updateUI() {
      this.updateImage();
      this.updatePrice();
      this.updateVariantId();
      this.updateAvailability();
      this.updateTitle();
      this.updateSku();
      this.updateDescription();
      this.updateURL();
    }

    updateTitle() {
      // Update dynamic title based on selected options
      const titleKarat = this.container.querySelector('[data-title-karat]');
      const titleColor = this.container.querySelector('[data-title-color]');
      const titleWidth = this.container.querySelector('[data-title-width]');
      const titleFinish = this.container.querySelector('[data-title-finish]');

      if (!this.currentVariant) return;

      // Update karat
      if (titleKarat) {
        const karat = this.currentProductKarat || '';
        titleKarat.textContent = karat;
      }

      // Update color
      if (titleColor) {
        const colorIndex = this.data.colorOptionIndex;
        let colorValue = '';
        if (colorIndex !== null && this.currentVariant.options) {
          colorValue = this.currentVariant.options[colorIndex] || '';
        } else {
          // Try to get from color switcher display
          const colorDisplay = this.container.querySelector('[data-selected-color]');
          if (colorDisplay) {
            colorValue = colorDisplay.textContent;
          }
        }
        titleColor.textContent = colorValue;
      }

      // Update width
      if (titleWidth) {
        const widthIndex = this.data.widthOptionIndex;
        if (widthIndex !== null && this.currentVariant.options) {
          titleWidth.textContent = this.currentVariant.options[widthIndex] || '';
        } else {
          // Try to get from width dropdown
          const widthSelect = this.container.querySelector('[data-width-selector]');
          if (widthSelect) {
            titleWidth.textContent = widthSelect.options[widthSelect.selectedIndex]?.textContent || '';
          }
        }
      }

      // Update finish/style
      if (titleFinish) {
        const finishIndex = this.data.finishOptionIndex;
        if (finishIndex !== null && this.currentVariant.options) {
          titleFinish.textContent = this.currentVariant.options[finishIndex] || '';
        }
      }
    }

    updateSku() {
      // Update SKU in description
      const skuElement = this.container.querySelector('[data-sku]');
      if (skuElement && this.currentVariant) {
        skuElement.textContent = this.currentVariant.sku || '';
      }
    }

    updateImage() {
      const mainImage = document.getElementById('band-builder-main-image');
      if (!mainImage || !this.currentVariant) return;

      let imageSrc = null;

      // Try to get variant-specific image
      if (this.currentVariant.featured_image) {
        imageSrc = this.currentVariant.featured_image.src || this.currentVariant.featured_image;
      } else if (this.currentVariant.image) {
        imageSrc = this.currentVariant.image.src || this.currentVariant.image;
      }

      if (imageSrc) {
        // Add loading state
        mainImage.classList.add('is-loading');
        
        // Preload image
        const img = new Image();
        img.onload = () => {
          mainImage.src = imageSrc;
          mainImage.classList.remove('is-loading');
          // Update floating preview image
          this.updatePreviewImage();
        };
        img.onerror = () => {
          mainImage.classList.remove('is-loading');
        };
        img.src = imageSrc;
      }
    }

    updatePrice() {
      const priceElement = this.container.querySelector('[data-price]');
      if (!priceElement || !this.currentVariant) return;

      // Use variant price directly (karat is baked into the product)
      const price = this.formatMoney(this.currentVariant.price);
      priceElement.textContent = price;

      // Also update floating preview price if it exists
      if (this.previewPriceValue) {
        this.previewPriceValue.textContent = price;
      }
    }

    updateVariantId() {
      const variantInput = document.getElementById('band-builder-variant-id');
      if (!variantInput || !this.currentVariant) return;

      variantInput.value = this.currentVariant.id;
    }

    updateAvailability() {
      const addToCartBtn = this.container.querySelector('.band-builder__btn--add-to-cart');
      const headerCartBtn = this.container.querySelector('[data-header-add-to-cart]');

      if (this.currentVariant && this.currentVariant.available) {
        if (addToCartBtn) {
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = 'ADD TO CART';
        }
        if (headerCartBtn) {
          headerCartBtn.disabled = false;
          headerCartBtn.textContent = 'ADD TO CART';
        }
      } else {
        if (addToCartBtn) {
          addToCartBtn.disabled = true;
          addToCartBtn.textContent = 'SOLD OUT';
        }
        if (headerCartBtn) {
          headerCartBtn.disabled = true;
          headerCartBtn.textContent = 'SOLD OUT';
        }
      }
    }

    async updateDescription() {
      const descriptionEl = this.container.querySelector('.band-builder__description');
      if (!descriptionEl || !this.currentVariant) return;

      // Check if variant has a custom description in metafields
      let variantDescription = this.getVariantDescription(this.currentVariant);
      
      // If not found in initial data, try fetching via Storefront API
      if (!variantDescription && this.currentVariant.id) {
        variantDescription = await this.fetchVariantDescriptionFromAPI(this.currentVariant.id);
      }
      
      // Build dynamic description based on selected variants
      let finalDescription = this.buildDynamicDescription();
      
      if (variantDescription) {
        // Append variant-specific details after the dynamic description
        finalDescription += '<p class="band-builder__variant-details">' + variantDescription + '</p>';
      }
      
      descriptionEl.innerHTML = finalDescription;
    }

    buildDynamicDescription() {
      if (!this.currentVariant) {
        // Fallback description if no variant data
        return '<p>This designer wedding band brings a fresh, minimalist approach to timeless design. Crafted from premium materials with meticulous attention to detail, it\'s made to last—whether you wear it solo or stack it with your engagement ring.</p>';
      }

      const options = this.data.optionsWithValues || [];
      const optionDetails = {};

      // Collect selected option values with their option names
      options.forEach((optionObj, index) => {
        const optionName = optionObj.name;
        const selectedValue = this.currentSelections[index];
        
        if (selectedValue && optionName) {
          optionDetails[optionName.toLowerCase()] = {
            name: optionName,
            value: selectedValue
          };
        }
      });

      // Get karat information - check multiple sources
      let karatInfo = null;
      
      // First try: Look in optionDetails for karat options (exact match to avoid "metal color")
      for (const key in optionDetails) {
        if (key.includes('karat') || key === 'gold' || key === 'metal') {
          karatInfo = optionDetails[key].value;
          break;
        }
      }
      
      // Second try: Use karatOptionIndex from data
      if (!karatInfo && this.data.karatIsVariant) {
        const karatIndex = this.data.karatOptionIndex;
        if (karatIndex !== null && this.currentSelections[karatIndex]) {
          karatInfo = this.currentSelections[karatIndex];
        }
      }
      
      // Third try: Use product metafield (includes karat extracted from title)
      if (!karatInfo && this.currentProductKarat) {
        karatInfo = this.currentProductKarat;
      }

      // Build description paragraphs
      let description = '';

      // Opening paragraph - warm and inviting
      description += '<p>This designer wedding band brings a fresh, minimalist approach to timeless design. Crafted from premium materials with meticulous attention to detail, it\'s made to last—whether you wear it solo or stack it with your engagement ring.</p>';

      // Variant-specific details paragraph
      const variantDetails = [];
      
      // Color details
      const colorOption = optionDetails['color'];
      if (colorOption) {
        const colorValue = colorOption.value.toLowerCase();
        let colorDescription = '';
        
        if (colorValue.includes('white gold') || colorValue.includes('white')) {
          colorDescription = 'The elegant white gold finish offers a contemporary sophistication';
        } else if (colorValue.includes('yellow gold') || colorValue.includes('yellow')) {
          colorDescription = 'The warm yellow gold brings a classic, timeless elegance';
        } else if (colorValue.includes('rose gold') || colorValue.includes('rose')) {
          colorDescription = 'The romantic rose gold adds a touch of warmth and modern femininity';
        } else if (colorValue.includes('platinum')) {
          colorDescription = 'The luxurious platinum provides exceptional durability and a cool, refined aesthetic';
        } else if (colorValue.includes('two-tone') || colorValue.includes('two tone')) {
          colorDescription = 'The two-tone design creates a striking contrast that adds visual interest';
        } else {
          colorDescription = `The ${colorOption.value.toLowerCase()} finish offers a distinctive character`;
        }
        
        variantDetails.push(colorDescription);
      }

      // Finish/Shank Style details (use actual option name)
      // Check for various option name variations (finish, style, shank style, etc.)
      let finishOption = null;
      for (const key in optionDetails) {
        if (key.includes('finish') || key.includes('style') || key.includes('shank')) {
          finishOption = optionDetails[key];
          break;
        }
      }
      
      if (finishOption) {
        const finishValue = finishOption.value.toLowerCase();
        let finishDescription = '';
        const optionLabel = finishOption.name; // Use actual option name (Finish, Style, Shank Style, etc.)
        
        if (finishValue.includes('satin')) {
          finishDescription = `The ${optionLabel.toLowerCase()} features a soft satin texture that catches light beautifully`;
        } else if (finishValue.includes('polish') || finishValue.includes('shiny')) {
          finishDescription = `The polished ${optionLabel.toLowerCase()} creates a brilliant, mirror-like surface`;
        } else if (finishValue.includes('scratch') || finishValue.includes('brushed')) {
          finishDescription = `The brushed ${optionLabel.toLowerCase()} offers a subtle, matte finish with contemporary appeal`;
        } else if (finishValue.includes('hammered')) {
          finishDescription = `The hammered ${optionLabel.toLowerCase()} adds unique texture and character`;
        } else if (finishValue.includes('matte')) {
          finishDescription = `The matte ${optionLabel.toLowerCase()} provides a modern, understated elegance`;
        } else {
          finishDescription = `The ${finishValue} ${optionLabel.toLowerCase()} adds distinctive character to the design`;
        }
        
        variantDetails.push(finishDescription);
      }

      // Width details
      const widthOption = optionDetails['width'];
      if (widthOption) {
        variantDetails.push(`The ${widthOption.value.toLowerCase()} width provides a comfortable, balanced fit`);
      }

      // Karat details
      if (karatInfo) {
        variantDetails.push(`Crafted in ${karatInfo}, ensuring lasting quality and value`);
      }

      // Add variant details paragraph if we have any
      if (variantDetails.length > 0) {
        description += '<p>' + variantDetails.join('. ') + '.</p>';
      }

      return description;
    }

    getVariantDescription(variant) {
      // Try to get from variant metafields
      if (variant.metafields && variant.metafields.custom && variant.metafields.custom.variant_description) {
        return variant.metafields.custom.variant_description;
      }

      // Try alternative metafield structure
      if (variant.metafields) {
        for (const key in variant.metafields) {
          if (key === 'variant_description' || key === 'description') {
            return variant.metafields[key];
          }
        }
      }

      return null;
    }

    async fetchVariantDescriptionFromAPI(variantId) {
      // Fallback: Fetch variant description via Storefront API
      const query = `
        query getVariant($id: ID!) {
          node(id: $id) {
            ... on ProductVariant {
              metafield(namespace: "custom", key: "variant_description") {
                value
              }
            }
          }
        }
      `;

      try {
        const response = await fetch('/api/2024-01/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': window.Shopify?.storefrontAccessToken || ''
          },
          body: JSON.stringify({
            query: query,
            variables: { id: `gid://shopify/ProductVariant/${variantId}` }
          })
        });

        const data = await response.json();
        return data?.data?.node?.metafield?.value || null;
      } catch (error) {
        console.warn('Could not fetch variant description from API:', error);
        return null;
      }
    }

    updateURL() {
      if (!this.currentVariant || !window.history.replaceState) return;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', this.currentVariant.id);
      
      // Also store current option selections in URL (for karat product switching)
      const options = this.data.options || [];
      options.forEach((optionName, index) => {
        const value = this.currentSelections[index];
        if (value) {
          url.searchParams.set(optionName, value);
        }
      });
      
      window.history.replaceState({}, '', url.toString());
    }

    formatMoney(cents) {
      const amount = (cents / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      // Try to use shop's money format
      if (this.data.moneyFormat) {
        return this.data.moneyFormat.replace('{{amount}}', amount).replace('{{ amount }}', amount);
      }
      
      return '$' + amount;
    }

    openSaveModal() {
      const modal = document.getElementById('band-builder-save-modal');
      if (!modal) return;

      // Populate preview summary
      this.populateSavePreviewSummary();

      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Focus first input
      setTimeout(() => {
        const firstInput = modal.querySelector('input');
        if (firstInput) firstInput.focus();
      }, 100);
    }

    populateSavePreviewSummary() {
      const summary = document.getElementById('save-preview-summary');
      if (!summary) return;

      let html = '';
      
      // Get current option values
      const options = this.data.options || [];
      options.forEach((optionName, index) => {
        const value = this.currentSelections[index];
        if (value) {
          html += `
            <div class="band-builder-modal__preview-item">
              <span class="band-builder-modal__preview-label">${optionName}:</span>
              <span class="band-builder-modal__preview-value">${value}</span>
            </div>
          `;
        }
      });

      // Add Karat (this product's karat)
      if (this.currentProductKarat) {
        const karatOption = this.availableKarats ? this.availableKarats.find(k => k.is_current) : null;
        const karatDisplay = karatOption ? karatOption.display_name : this.currentProductKarat;
        html += `
          <div class="band-builder-modal__preview-item">
            <span class="band-builder-modal__preview-label">Karat:</span>
            <span class="band-builder-modal__preview-value">${karatDisplay}</span>
          </div>
        `;
      }

      // Add SKU if available
      if (this.currentVariant && this.currentVariant.sku) {
        html += `
          <div class="band-builder-modal__preview-item">
            <span class="band-builder-modal__preview-label">SKU:</span>
            <span class="band-builder-modal__preview-value">${this.currentVariant.sku}</span>
          </div>
        `;
      }

      summary.innerHTML = html;
    }

    openRequestModal() {
      const modal = document.getElementById('band-builder-request-modal');
      if (!modal) return;

      // Update hidden fields with current selection
      const productInput = modal.querySelector('input[name="contact[product]"]');
      if (productInput && this.currentVariant) {
        const sku = this.currentVariant.sku || this.currentVariant.title;
        productInput.value = `${document.title} - ${sku}`;
      }

      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Focus first input
      setTimeout(() => {
        const firstInput = modal.querySelector('input[type="text"], input[type="email"]');
        if (firstInput) firstInput.focus();
      }, 100);
    }

    closeModals(e) {
      if (e && e.target && !e.target.matches('[data-modal-close]') && !e.target.closest('[data-modal-close]')) {
        return;
      }

      document.querySelectorAll('.band-builder-modal').forEach(modal => {
        modal.setAttribute('aria-hidden', 'true');
      });
      document.body.style.overflow = '';
    }

    handleSavePreview(e) {
      e.preventDefault();

      const form = e.target;
      const email = form.querySelector('#save-email').value;
      const submitBtn = form.querySelector('button[type="submit"]');

      if (!email) return;

      // Show loading state
      const originalText = submitBtn.textContent;
      submitBtn.textContent = 'SAVING...';
      submitBtn.disabled = true;

      // Build configuration data
      const configData = {
        email: email,
        productUrl: window.location.href,
        productTitle: document.title,
        variant: this.currentVariant ? {
          id: this.currentVariant.id,
          sku: this.currentVariant.sku,
          title: this.currentVariant.title,
          price: this.formatMoney(this.currentVariant.price)
        } : null,
        karat: this.currentProductKarat,
        selections: {}
      };

      // Add option selections
      const options = this.data.options || [];
      options.forEach((optionName, index) => {
        configData.selections[optionName] = this.currentSelections[index] || '';
      });

      // Add karat to selections
      const karatOption = this.availableKarats ? this.availableKarats.find(k => k.is_current) : null;
      configData.selections['Karat'] = karatOption ? karatOption.display_name : this.currentProductKarat;

      // Store in localStorage as backup
      try {
        const savedConfigs = JSON.parse(localStorage.getItem('masina_saved_configs') || '[]');
        savedConfigs.push({
          ...configData,
          savedAt: new Date().toISOString()
        });
        localStorage.setItem('masina_saved_configs', JSON.stringify(savedConfigs));
      } catch (err) {
        console.warn('Could not save to localStorage', err);
      }

      // Simulate email send (in production, this would be an API call)
      setTimeout(() => {
        submitBtn.textContent = 'SAVED!';
        submitBtn.classList.add('is-success');

        setTimeout(() => {
          this.closeModals();
          submitBtn.textContent = originalText;
          submitBtn.classList.remove('is-success');
          submitBtn.disabled = false;
          form.reset();
        }, 1500);
      }, 1000);
    }

    handleZoom() {
      const mainImage = document.getElementById('band-builder-main-image');
      if (!mainImage) return;

      // If photoswipe is available (from theme), use it
      if (typeof PhotoSwipeLightbox !== 'undefined' || typeof PhotoSwipe !== 'undefined') {
        // Trigger theme's photoswipe
        const event = new CustomEvent('photoswipe:open', {
          detail: {
            src: mainImage.src.replace(/(_\d+x\d+|_\d+x|x\d+)/, ''),
            width: 1200,
            height: 1200,
            alt: mainImage.alt
          }
        });
        document.dispatchEvent(event);
        return;
      }

      // Fallback: open image in new tab
      window.open(mainImage.src.replace(/(_\d+x\d+|_\d+x|x\d+)/, '_1200x1200'), '_blank');
    }

    handleFormSubmit(e) {
      if (!this.currentVariant || !this.currentVariant.available) {
        e.preventDefault();
        return;
      }

      // Form will submit normally to /cart/add
      // Optionally, we could handle this with AJAX
      const submitBtn = e.target.querySelector('.band-builder__btn--add-to-cart');
      if (submitBtn) {
        submitBtn.textContent = 'ADDING...';
        submitBtn.disabled = true;
      }

      // Also update header button
      const headerBtn = this.container.querySelector('[data-header-add-to-cart]');
      if (headerBtn) {
        headerBtn.textContent = 'ADDING...';
        headerBtn.disabled = true;
      }
    }

    handleHeaderAddToCart() {
      if (!this.currentVariant || !this.currentVariant.available) {
        return;
      }

      // Submit the form
      const form = document.getElementById('band-builder-form');
      if (form) {
        // Update button states
        const headerBtn = this.container.querySelector('[data-header-add-to-cart]');
        if (headerBtn) {
          headerBtn.textContent = 'ADDING...';
          headerBtn.disabled = true;
        }

        const submitBtn = this.container.querySelector('.band-builder__btn--add-to-cart');
        if (submitBtn) {
          submitBtn.textContent = 'ADDING...';
          submitBtn.disabled = true;
        }

        form.submit();
      }
    }

    handleDescriptionToggle() {
      const toggle = this.container.querySelector('[data-description-toggle]');
      const content = this.container.querySelector('[data-description-content]');
      
      if (!toggle || !content) return;

      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      
      toggle.setAttribute('aria-expanded', !isExpanded);
      content.setAttribute('data-expanded', !isExpanded);
    }

    initFloatingPreview() {
      // Handle resize events
      this.handleResize = this.handleResize.bind(this);
      window.addEventListener('resize', this.debounce(this.handleResize, 250));

      // Only initialize on mobile
      if (window.innerWidth > 989) return;

      this.createFloatingPreview();
      this.setupScrollObserver();
    }

    handleResize() {
      const isMobile = window.innerWidth <= 989;
      
      if (isMobile && !this.floatingPreview) {
        // Switched to mobile, create preview
        this.createFloatingPreview();
        this.setupScrollObserver();
      } else if (!isMobile && this.floatingPreview) {
        // Switched to desktop, remove preview
        this.destroyFloatingPreview();
      }
    }

    destroyFloatingPreview() {
      if (this.scrollObserver) {
        this.scrollObserver.disconnect();
        this.scrollObserver = null;
      }
      if (this.floatingPreview) {
        this.floatingPreview.remove();
        this.floatingPreview = null;
        this.previewImage = null;
        this.previewToggle = null;
      }
    }

    debounce(func, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    }

    createFloatingPreview() {
      const mainImage = document.getElementById('band-builder-main-image');
      if (!mainImage) return;

      // Get current price
      const priceElement = this.container.querySelector('[data-price]');
      const currentPrice = priceElement ? priceElement.textContent : '';

      // Create floating preview container
      this.floatingPreview = document.createElement('div');
      this.floatingPreview.className = 'band-builder__floating-preview';
      this.floatingPreview.innerHTML = `
        <div class="band-builder__preview-image-container">
          <img class="band-builder__preview-image" src="${mainImage.src}" alt="Preview">
          <div class="band-builder__preview-price">
            <span class="band-builder__preview-price-value">${currentPrice}</span>
            <span class="band-builder__preview-price-disclaimer">*Estimate, subject to gold prices</span>
          </div>
        </div>
        <button class="band-builder__preview-toggle" aria-label="Toggle preview" type="button">
          <svg class="band-builder__preview-toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="18 15 12 9 6 15"></polyline>
          </svg>
        </button>
      `;

      document.body.appendChild(this.floatingPreview);

      // Store references
      this.previewImage = this.floatingPreview.querySelector('.band-builder__preview-image');
      this.previewToggle = this.floatingPreview.querySelector('.band-builder__preview-toggle');
      this.previewPriceValue = this.floatingPreview.querySelector('.band-builder__preview-price-value');

      // Bind toggle click
      this.previewToggle.addEventListener('click', () => this.toggleFloatingPreview());

      // Click on image scrolls back to top
      const imageContainer = this.floatingPreview.querySelector('.band-builder__preview-image-container');
      imageContainer.addEventListener('click', () => {
        const mediaSection = this.container.querySelector('.band-builder__media');
        if (mediaSection) {
          mediaSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
      imageContainer.style.cursor = 'pointer';
    }

    setupScrollObserver() {
      const imageWrapper = this.container.querySelector('.band-builder__image-wrapper');
      if (!imageWrapper || !this.floatingPreview) return;

      // Use Intersection Observer to detect when image is out of view
      this.scrollObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) {
            // Image is out of view - show floating preview
            this.showFloatingPreview();
          } else {
            // Image is in view - hide floating preview
            this.hideFloatingPreview();
          }
        });
      }, {
        threshold: 0,
        rootMargin: '-50px 0px 0px 0px'
      });

      this.scrollObserver.observe(imageWrapper);
    }

    showFloatingPreview() {
      if (!this.floatingPreview) return;
      
      this.floatingPreview.classList.add('is-visible');
      
      // Add pulse animation on first show
      if (!this.hasShownPreview) {
        this.hasShownPreview = true;
        this.previewToggle.classList.add('is-pulsing');
        setTimeout(() => {
          this.previewToggle.classList.remove('is-pulsing');
        }, 3000);
      }
    }

    hideFloatingPreview() {
      if (!this.floatingPreview) return;
      this.floatingPreview.classList.remove('is-visible');
    }

    toggleFloatingPreview() {
      if (!this.floatingPreview) return;
      this.floatingPreview.classList.toggle('is-collapsed');
    }

    updatePreviewImage() {
      if (!this.previewImage) return;
      
      const mainImage = document.getElementById('band-builder-main-image');
      if (mainImage) {
        this.previewImage.src = mainImage.src;
      }

      // Also update the price in the preview
      if (this.previewPriceValue) {
        const priceElement = this.container.querySelector('[data-price]');
        if (priceElement) {
          this.previewPriceValue.textContent = priceElement.textContent;
        }
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new BandBuilder());
  } else {
    new BandBuilder();
  }

  // Re-initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('.band-builder')) {
      new BandBuilder();
    }
  });
})();
